/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {Link as RouteLink} from 'react-router-dom'

// Components
import {
    Box,
    Center,
    Flex,
    Skeleton,
    Stack,
    Link,
    // Hooks
    useTheme
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import {ListMenuPopover} from './list-menu-popover'

// Others
import {categoryUrlBuilder} from '../../utils/url'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useTagManager from '../../hooks/use-tag-manager'
import useMonetate from '../../hooks/use-monetate'
import useNavigationHandler, {getNavigationProps} from '../../hooks/use-navigation-handler'
import LoadingSpinner from '../loading-spinner'

// Constants
const MAXIMUM_NUMBER_COLUMNS = 5

/**
 * This is the navigation component used for desktop devices. Holds the site navigation,
 * providing users with a way to access all product categories and other important pages.
 * The submenus are open when the user moves the mouse over the trigger and for A11y when
 * users use the keyboard Tab key to focus over the chevron icon and press Enter.
 *
 * @param maxColumns The maximum number of columns that we want to use per row inside the ListMenu.
 * @param root
 */
const ListMenu = ({
    root,
    contentComponent,
    itemsKey,
    itemsCountKey,
    maxColumns = MAXIMUM_NUMBER_COLUMNS
}) => {
    const theme = useTheme()
    const [ariaBusy, setAriaBusy] = useState(true)
    const [activeLink, setActiveLink] = useState()
    const tagManager = useTagManager()
    const monetate = useMonetate()
    const [showNavSkeleton, setShowNavSkeleton] = useState(false)
    const {
        app: {commerceAPI}
    } = getConfig()
    const {hostUrl} = commerceAPI.storefront
    const intl = useIntl()

    const {baseStyle} = theme.components.ListMenu
    const items = root?.[itemsKey]

    useEffect(() => {
        setAriaBusy(false)
    }, [])

    useEffect(() => {
        if (!items) {
            setShowNavSkeleton(true)
            window.setTimeout(() => {
                setShowNavSkeleton(false)
            }, 2000) // For the edge case (in which no category is available, display skeleton for 2 seconds)
        }
    }, [items])
    const spinnerRef = useRef(null)
    const handleNavigation = useNavigationHandler({spinnerRef})

    return (
        <nav
            id="list-menu"
            aria-label={intl.formatMessage({
                id: 'list_menu.nav.assistive_msg',
                defaultMessage: 'Main navigation'
            })}
            aria-live="polite"
            aria-busy={ariaBusy}
            aria-atomic="true"
        >
            <Box ref={spinnerRef} className="hide-element">
                <LoadingSpinner position="fixed" top="auto" background="transparent" />
            </Box>
            <Flex {...baseStyle.container} className="categories-flex">
                {items ? (
                    <Stack
                        direction={'row'}
                        spacing={{base: 0, lg: 1, '2lg': 0}}
                        {...baseStyle.stackContainer}
                        className="categories-stack"
                    >
                        {items
                            ?.filter(({c_showInMenu}) => c_showInMenu)
                            ?.map?.((item) => {
                                const {id, name, c_alternativeUrl} = item
                                const itemsCount =
                                    item[itemsCountKey] || item[itemsKey]?.length || 0
                                const redirectionProps =
                                    !c_alternativeUrl ||
                                    c_alternativeUrl?.indexOf('/yellow-rose') === 0
                                        ? {to: categoryUrlBuilder(item), as: RouteLink}
                                        : {href: hostUrl + c_alternativeUrl}
                                const itemHref = redirectionProps.to || redirectionProps.href
                                const navProps = getNavigationProps(
                                    itemHref,
                                    Boolean(redirectionProps.href)
                                )
                                return (
                                    <Box
                                        key={id}
                                        data-id={id}
                                        className={`menu-popover-wrapper menu-popover-wrapper-${id}`}
                                    >
                                        {/*** Desktop Category - L1 (with no child categories) ***/}
                                        {itemsCount > 0 ? (
                                            <ListMenuPopover
                                                key={id}
                                                maxColumns={maxColumns}
                                                item={item}
                                                name={name}
                                                items={item?.[itemsKey]}
                                                itemsKey={itemsKey}
                                                contentComponent={contentComponent}
                                            />
                                        ) : (
                                            <Link
                                                {...redirectionProps}
                                                onMouseOver={setActiveLink.bind(this, id)}
                                                className={`nav-link nav-link-${id}`}
                                                marginTop={4}
                                                marginBottom={2}
                                                onMouseOut={setActiveLink.bind(this)}
                                                onClick={(event) => {
                                                    handleNavigation(event, navProps, () => {
                                                        tagManager.navigationMain(
                                                            'header',
                                                            name.toLowerCase(),
                                                            undefined,
                                                            undefined
                                                        )
                                                        monetate.setCategoriesViewed(name)
                                                        monetate.setNavigationClicks(name)
                                                    })
                                                }}
                                                {...baseStyle.listMenuTriggerLink}
                                                {...{name: name + ' __'}}
                                                {...(activeLink === id
                                                    ? baseStyle.listMenuTriggerlessLinkActive
                                                    : {})}
                                            >
                                                {name}
                                            </Link>
                                        )}
                                    </Box>
                                )
                            })}
                    </Stack>
                ) : (
                    <Center p="2">
                        {showNavSkeleton && (
                            <>
                                <Skeleton height="20px" width="180px" />{' '}
                                <Skeleton marginLeft="20px" height="20px" width="180px" />
                                <Skeleton marginLeft="20px" height="20px" width="180px" />
                            </>
                        )}
                    </Center>
                )}
            </Flex>
        </nav>
    )
}

ListMenu.displayName = 'ListMenu'

ListMenu.propTypes = {
    root: PropTypes.object,
    /**
     * The maximum number of columns that we want to use per row in the menu.
     */
    maxColumns: PropTypes.number,
    /**
     * Customize the property representing the items.
     */
    itemsKey: PropTypes.string,
    /**
     * Cusomtize the property representing the items count.
     */
    itemsCountKey: PropTypes.string,
    /**
     * Customize the component used to render the list menu item
     */
    contentComponent: PropTypes.elementType
}

export {ListMenu}
